/**
 * VÉRAULT Portal — Advisor: Settings v1 (Pass C · S2)
 *
 * First functional Settings page. Replaces the PolishedPlaceholder.
 * Scope (per user direction):
 *   · Brand profile (company name, logo, footer, planning fee, terms URLs)
 *   · Calendar integration (Nylas/Google connect/disconnect, status)
 *   · Connected accounts visibility
 *   · Integrations status (LLM key, OAuth providers, Pexels, etc.)
 *   · Team (read-only for now)
 *
 * Deferred to a later pass: key rotation, advanced audit log, enterprise security.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  CalendarClock,
  Plug,
  Users,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Loader2,
  Save,
  Link as LinkIcon,
  Unlink,
  ShieldCheck,
} from "lucide-react";
import {
  PageHeader,
  Surface,
  Heading,
  EditorialRule,
  StatusTag,
  PortalButton,
  Skeleton,
  Empty,
  api,
  motion as portalMotion,
} from "../../index";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "sonner";

const { fadeInUp, stagger } = portalMotion;

const inputClass =
  "w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white transition-colors placeholder:text-[hsl(var(--foreground-muted))]";
const labelClass =
  "font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5 block";

const TABS = [
  { id: "brand", label: "Brand profile", icon: Building2 },
  { id: "calendar", label: "Calendar", icon: CalendarClock },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "team", label: "Team", icon: Users },
  { id: "audit", label: "Audit log", icon: ShieldCheck },
];

export default function AdvisorSettings() {
  const [tab, setTab] = React.useState("brand");
  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Settings · Atelier configuration"
          title="The quiet machinery of the brand."
          lede="Everything that controls how VÉRAULT presents itself, who's connected, and what's plugged in."
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded={false} className="overflow-hidden">
          <div className="flex" data-testid="settings-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`settings-tab-${t.id}`}
                  className={`flex-1 px-5 py-3.5 text-left transition-colors border-r last:border-r-0 border-[rgba(0,0,0,0.05)] ${
                    active ? "bg-white" : "hover:bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon
                      className={`size-3.5 ${active ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}
                      strokeWidth={1.5}
                    />
                    <span
                      className={`font-mono text-[10.5px] uppercase tracking-[0.22em] ${
                        active ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"
                      }`}
                    >
                      {t.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Surface>
      </motion.div>

      <motion.div variants={fadeInUp}>
        {tab === "brand" && <BrandTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "team" && <TeamTab />}
        {tab === "audit" && <AuditTab />}
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded className="!py-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Recently deleted · Trash
              </p>
              <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                Restore or permanently purge soft-deleted experiences and clients. Items auto-purge after 30 days.
              </p>
            </div>
            <a
              href="/portal/advisor/settings/trash"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:underline"
              data-testid="settings-trash-link"
            >
              Open Trash &rarr;
            </a>
          </div>
        </Surface>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded className="!py-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Developer · Maintenance
              </p>
              <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
                Sweep test data accumulated during development. Preview before delete.
              </p>
            </div>
            <a
              href="/portal/advisor/settings/test-data-cleanup"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-terracotta))] hover:underline"
              data-testid="settings-cleanup-link"
            >
              Open Test Data Cleanup &rarr;
            </a>
          </div>
        </Surface>
      </motion.div>
    </motion.div>
  );
}

// --------------------------------------------------------------- Display Identity (Wave 11i · Phase 7)
function DisplayIdentitySection() {
  const { user, checkSession } = useAuth();
  const [name, setName] = React.useState(user?.name || "");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { setName(user?.name || ""); }, [user?.name, user?.id]);

  const save = async () => {
    const next = (name || "").trim();
    if (!next) {
      toast.error("Display identity can't be empty");
      return;
    }
    if (next === (user?.name || "").trim()) {
      toast.success("No change to save.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me", { name: next });
      await checkSession();
      toast.success("Display identity updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save identity.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="settings-display-identity">
      <EditorialRule label="Display identity" />
      <Surface variant="paper" padded>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <span className={labelClass}>Advisor handle</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Antonia@Verault.Travels"
              className={inputClass}
              data-testid="settings-identity-input"
              maxLength={120}
            />
            <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1.5">
              This handle appears on the DEV pill, slot editor, advisor identity badges,
              and the concierge thread your client sees. Try something memorable —
              e.g. <span className="font-mono not-italic">Antonia@Verault.Travels</span>.
            </p>
          </div>
          <PortalButton
            variant="primary"
            size="sm"
            icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            onClick={save}
            disabled={busy}
            data-testid="settings-identity-save"
          >
            {busy ? "Saving" : "Save"}
          </PortalButton>
        </div>
      </Surface>
    </div>
  );
}


// --------------------------------------------------------------- Brand
function BrandTab() {
  const [settings, setSettings] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api
      .get("/travels/settings")
      .then(({ data }) => setSettings(data))
      .catch(() => setSettings({}));
  }, []);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = {
        company_name: settings.company_name,
        logo_url: settings.logo_url,
        footer_text: settings.footer_text,
        default_planning_fee: Number(settings.default_planning_fee) || 0,
        planning_fee_description: settings.planning_fee_description,
        default_terms_url: settings.default_terms_url,
        default_contract_url: settings.default_contract_url,
        default_card_auth_url: settings.default_card_auth_url,
      };
      await api.patch("/travels/settings", body);
      toast.success("Brand profile saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-8" data-testid="settings-brand-tab">
      <DisplayIdentitySection />
      <EditorialRule label="Brand identity" />
      <Surface variant="paper" padded>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <span className={labelClass}>Company name</span>
            <input
              className={inputClass}
              value={settings.company_name || ""}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              data-testid="settings-brand-company"
            />
          </div>
          <div>
            <span className={labelClass}>Footer text (PDFs, emails)</span>
            <input
              className={inputClass}
              value={settings.footer_text || ""}
              onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
              data-testid="settings-brand-footer"
            />
          </div>
          <div className="sm:col-span-2">
            <span className={labelClass}>Logo URL</span>
            <input
              className={inputClass}
              placeholder="https://…"
              value={settings.logo_url || ""}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              data-testid="settings-brand-logo"
            />
          </div>
        </div>
      </Surface>

      <EditorialRule label="Financial defaults" />
      <Surface variant="paper" padded>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <span className={labelClass}>Default planning fee (USD)</span>
            <input
              type="number"
              className={inputClass}
              value={settings.default_planning_fee ?? ""}
              onChange={(e) => setSettings({ ...settings, default_planning_fee: e.target.value })}
              data-testid="settings-brand-fee"
            />
          </div>
          <div>
            <span className={labelClass}>Planning fee description</span>
            <input
              className={inputClass}
              value={settings.planning_fee_description || ""}
              onChange={(e) => setSettings({ ...settings, planning_fee_description: e.target.value })}
              data-testid="settings-brand-fee-desc"
            />
          </div>
        </div>
      </Surface>

      <EditorialRule label="Standard document URLs" />
      <Surface variant="paper" padded>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <span className={labelClass}>Default terms URL</span>
            <input
              className={inputClass}
              placeholder="https://…"
              value={settings.default_terms_url || ""}
              onChange={(e) => setSettings({ ...settings, default_terms_url: e.target.value })}
            />
          </div>
          <div>
            <span className={labelClass}>Default contract URL</span>
            <input
              className={inputClass}
              placeholder="https://…"
              value={settings.default_contract_url || ""}
              onChange={(e) => setSettings({ ...settings, default_contract_url: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <span className={labelClass}>Default card-authorization URL</span>
            <input
              className={inputClass}
              placeholder="https://…"
              value={settings.default_card_auth_url || ""}
              onChange={(e) => setSettings({ ...settings, default_card_auth_url: e.target.value })}
            />
          </div>
        </div>
      </Surface>

      <div className="flex justify-end">
        <PortalButton
          onClick={save}
          disabled={busy}
          variant="primary"
          icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
          data-testid="settings-brand-save"
        >
          {busy ? "Saving" : "Save brand profile"}
        </PortalButton>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- Calendar
function CalendarTab() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/settings/calendar");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/settings/calendar/connect");
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else if (data?.mode === "mock") {
        toast.message(data.message || "Google Calendar not configured yet.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't initiate connection.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings/calendar/disconnect");
      toast.success("Calendar disconnected.");
      await refresh();
    } catch (e) {
      toast.error("Couldn't disconnect.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <Skeleton className="h-64" />;
  const connected = !!status.connected;

  return (
    <div className="space-y-8" data-testid="settings-calendar-tab">
      <EditorialRule label="Operational calendar" />
      <Surface variant="paper" padded>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Heading level="eyebrow">Google Calendar</Heading>
            <p className="mt-1 font-serif text-lg">
              {connected ? "Connected" : "Not connected"}
            </p>
            <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
              {connected
                ? `Trip events sync to the calendar named "${status.calendar_name || "VÉRAULT"}".`
                : "Connect to mirror trip departures, returns, and key milestones to your team's calendar."}
            </p>
            {status.last_connected_at && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Last connected · {new Date(status.last_connected_at).toLocaleDateString("en-US")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusTag tone={connected ? "sage" : "neutral"}>
              {connected ? "Active" : "Inactive"}
            </StatusTag>
            {connected ? (
              <PortalButton
                variant="ghost"
                size="sm"
                icon={<Unlink className="size-3.5" />}
                onClick={disconnect}
                disabled={busy}
                data-testid="settings-calendar-disconnect"
              >
                Disconnect
              </PortalButton>
            ) : (
              <PortalButton
                variant="primary"
                size="sm"
                icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : <LinkIcon className="size-3.5" />}
                onClick={connect}
                disabled={busy}
                data-testid="settings-calendar-connect"
              >
                {busy ? "Connecting" : "Connect Google Calendar"}
              </PortalButton>
            )}
          </div>
        </div>
      </Surface>

      {status.mode === "mock" && (
        <Surface variant="paper" padded>
          <div className="flex items-start gap-3">
            <AlertCircle className="size-4 text-[hsl(var(--accent-gold))] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                Configuration needed
              </p>
              <p className="mt-1 font-body text-[13.5px]">
                Set <code className="font-mono text-[12px] bg-[hsl(var(--background-paper))] px-1 py-0.5 rounded">GOOGLE_CALENDAR_CLIENT_ID</code> and{" "}
                <code className="font-mono text-[12px] bg-[hsl(var(--background-paper))] px-1 py-0.5 rounded">GOOGLE_CALENDAR_CLIENT_SECRET</code> to enable OAuth.
              </p>
            </div>
          </div>
        </Surface>
      )}
    </div>
  );
}

// --------------------------------------------------------------- Integrations
function IntegrationsTab() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    api
      .get("/settings/integrations")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-6" data-testid="settings-integrations-tab">
      <EditorialRule label={`${data.connected_count || 0} of ${data.total || 0} active`} />
      <Surface variant="default" padded={false} className="overflow-hidden">
        <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
          {(data.integrations || []).map((it) => (
            <li key={it.key} className="px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-serif text-[15px] text-[hsl(var(--foreground))]">{it.label}</p>
                <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))]">
                  {it.description}
                </p>
                {it.last_used_at && (
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                    Last used · {new Date(it.last_used_at).toLocaleDateString("en-US")}
                  </p>
                )}
              </div>
              <StatusTag tone={it.status === "active" ? "sage" : it.status === "warn" ? "gold" : "neutral"}>
                {it.status === "active" ? (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="size-3" strokeWidth={1.5} /> Active
                  </span>
                ) : it.status === "warn" ? (
                  <span className="inline-flex items-center gap-1">
                    <AlertCircle className="size-3" strokeWidth={1.5} /> {it.status_label || "Attention"}
                  </span>
                ) : (
                  it.status_label || "Inactive"
                )}
              </StatusTag>
            </li>
          ))}
        </ul>
      </Surface>

      {/* Phase 1.2 · Nylas Scheduler configuration (mock-ready). */}
      <NylasConfigSection />

      {/* Phase 1.3 · Resend / Auto-Reply configuration (mock-ready). */}
      <ResendConfigSection />
    </div>
  );
}

function ResendConfigSection() {
  const [settings, setSettings] = React.useState({
    resend_api_key: "",
    resend_from_name: "VÉRAULT Travels",
    resend_from_email: "concierge@verault.co",
    resend_reply_to: "",
    auto_reply_enabled: false,
    email_mode: "preview",
    resend_last_status: null,
    resend_last_status_at: null,
  });
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/travels/settings").then(({ data }) => {
      setSettings({
        resend_api_key: data?.resend_api_key || "",
        resend_from_name: data?.resend_from_name || "VÉRAULT Travels",
        resend_from_email: data?.resend_from_email || "concierge@verault.co",
        resend_reply_to: data?.resend_reply_to || "",
        auto_reply_enabled: !!data?.auto_reply_enabled,
        email_mode: data?.email_mode || "preview",
        resend_last_status: data?.resend_last_status || null,
        resend_last_status_at: data?.resend_last_status_at || null,
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/travels/settings", settings);
      toast.success("Resend configuration saved.");
    } catch {
      toast.error("Could not save Resend configuration.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const to = window.prompt("Send a test email to which address?");
    if (!to) return;
    try {
      const { data } = await api.post("/email-log/test", { to_email: to });
      toast.message(
        data.mode === "live" ? "Test queued for live delivery." : "Test logged in preview mode.",
        { description: `Status: ${data.status}` }
      );
    } catch {
      toast.error("Test email failed.");
    }
  };

  if (!loaded) return <Skeleton className="h-32" />;

  const isLive = settings.email_mode === "live" && settings.auto_reply_enabled && !!settings.resend_api_key;
  const statusTone = isLive ? "sage" : settings.resend_api_key ? "gold" : "neutral";

  return (
    <Surface variant="paper" padded data-testid="settings-resend-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
            Resend · Auto-reply
          </p>
          <Heading level="h4" className="mt-1">Consultation confirmations</Heading>
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Every public consultation submission is logged. Preview mode is the
            safe default — switch to live only when your Resend key is in.
          </p>
        </div>
        <StatusTag tone={statusTone}>
          {isLive ? "Live · sending" : settings.resend_api_key ? "Configured · preview" : "Not configured"}
        </StatusTag>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Resend API key</label>
          <input
            type="password"
            value={settings.resend_api_key}
            onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
            placeholder="re_…"
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-mono text-[12px]"
            data-testid="settings-resend-api-key"
          />
        </div>
        <div>
          <label className={labelClass}>From name</label>
          <input
            type="text"
            value={settings.resend_from_name}
            onChange={(e) => setSettings({ ...settings, resend_from_name: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-serif text-[14px]"
            data-testid="settings-resend-from-name"
          />
        </div>
        <div>
          <label className={labelClass}>From email</label>
          <input
            type="email"
            value={settings.resend_from_email}
            onChange={(e) => setSettings({ ...settings, resend_from_email: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-mono text-[12px]"
            data-testid="settings-resend-from-email"
          />
        </div>
        <div>
          <label className={labelClass}>Reply-to email</label>
          <input
            type="email"
            value={settings.resend_reply_to}
            onChange={(e) => setSettings({ ...settings, resend_reply_to: e.target.value })}
            placeholder="(optional)"
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-mono text-[12px]"
            data-testid="settings-resend-reply-to"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-resend-auto-reply-toggle">
          <input
            type="checkbox"
            checked={settings.auto_reply_enabled}
            onChange={(e) => setSettings({ ...settings, auto_reply_enabled: e.target.checked })}
            className="size-4 rounded border-[rgba(0,0,0,0.2)] text-[hsl(var(--accent-gold))]"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Auto-reply enabled
          </span>
        </label>
        <div className="inline-flex items-center gap-1 border border-[rgba(0,0,0,0.08)] rounded-full p-0.5">
          {["preview", "live"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setSettings({ ...settings, email_mode: m })}
              className={`px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] ${settings.email_mode === m ? "bg-[hsl(var(--foreground))] text-white" : "text-[hsl(var(--foreground-muted))]"}`}
              data-testid={`settings-resend-mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
        {settings.resend_last_status_at && (
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            Last · {settings.resend_last_status} · {new Date(settings.resend_last_status_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <PortalButton variant="primary" size="sm" onClick={save} disabled={saving} data-testid="settings-resend-save">
          {saving ? "Saving…" : "Save"}
        </PortalButton>
        <PortalButton variant="ghost" size="sm" onClick={sendTest} data-testid="settings-resend-test">
          Send test email
        </PortalButton>
      </div>
    </Surface>
  );
}

function NylasConfigSection() {
  const [settings, setSettings] = React.useState({
    nylas_api_key: "",
    nylas_signing_secret: "",
    nylas_scheduler_source_name: "Nyla",
    nylas_webhook_status: "not_configured",
    nylas_last_webhook_at: null,
  });
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/travels/settings").then(({ data }) => {
      setSettings({
        nylas_api_key: data?.nylas_api_key || "",
        nylas_signing_secret: data?.nylas_signing_secret || "",
        nylas_scheduler_source_name: data?.nylas_scheduler_source_name || "Nyla",
        nylas_webhook_status: data?.nylas_webhook_status || "not_configured",
        nylas_last_webhook_at: data?.nylas_last_webhook_at || null,
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/travels/settings", settings);
      toast.success("Nylas configuration saved.");
    } catch {
      toast.error("Could not save Nylas configuration.");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!settings.nylas_api_key) {
      toast.error("Add your Nylas API key first.");
      return;
    }
    try {
      // Persist whatever is in the form before testing so the backend reads the
      // value the operator just pasted (not a stale one).
      await api.patch("/travels/settings", settings);
      const { data } = await api.post("/integrations/nylas/test");
      if (data?.ok) {
        const appName = data?.application?.name || data?.application?.id || "application";
        toast.success(`Nylas connection verified · ${appName}`);
        // Refresh status pill from server in case backend bumped it to "configured".
        const refreshed = await api.get("/travels/settings");
        setSettings((s) => ({
          ...s,
          nylas_webhook_status: refreshed.data?.nylas_webhook_status || s.nylas_webhook_status,
        }));
      } else {
        toast.error(`Nylas test failed (${data?.status || "?"}): ${data?.error || "see server logs"}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || e?.message || "Connection test failed";
      toast.error(`Nylas test failed: ${msg}`);
    }
  };

  if (!loaded) return <Skeleton className="h-32" />;

  const statusTone =
    settings.nylas_webhook_status === "live" ? "sage" :
    settings.nylas_webhook_status === "configured" ? "gold" :
    "neutral";

  return (
    <Surface variant="paper" padded data-testid="settings-nylas-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
            Nylas Scheduler v3
          </p>
          <Heading level="h4" className="mt-1">Consultation intake</Heading>
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Paste keys when you have them — the consultation pipeline already works
            via the public intake mock until then.
          </p>
        </div>
        <StatusTag tone={statusTone}>
          {settings.nylas_webhook_status === "live" ? "Webhook live"
            : settings.nylas_webhook_status === "configured" ? "Configured · not verified"
            : "Not configured"}
        </StatusTag>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Nylas API key</label>
          <input
            type="password"
            value={settings.nylas_api_key || ""}
            onChange={(e) => setSettings({ ...settings, nylas_api_key: e.target.value })}
            placeholder="nyk_…"
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-mono text-[12px]"
            data-testid="settings-nylas-api-key"
          />
        </div>
        <div>
          <label className={labelClass}>Signing secret</label>
          <input
            type="password"
            value={settings.nylas_signing_secret || ""}
            onChange={(e) => setSettings({ ...settings, nylas_signing_secret: e.target.value })}
            placeholder="whsec_…"
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-mono text-[12px]"
            data-testid="settings-nylas-signing-secret"
          />
        </div>
        <div>
          <label className={labelClass}>Scheduler source name</label>
          <input
            type="text"
            value={settings.nylas_scheduler_source_name || ""}
            onChange={(e) => setSettings({ ...settings, nylas_scheduler_source_name: e.target.value })}
            placeholder="Nyla"
            className="w-full px-3 py-2 bg-white border border-[rgba(0,0,0,0.08)] rounded-md font-serif text-[14px]"
            data-testid="settings-nylas-source-name"
          />
        </div>
        <div>
          <label className={labelClass}>Last webhook received</label>
          <div className="px-3 py-2 bg-white border border-[rgba(0,0,0,0.05)] rounded-md font-mono text-[12px] text-[hsl(var(--foreground-muted))]">
            {settings.nylas_last_webhook_at
              ? new Date(settings.nylas_last_webhook_at).toLocaleString()
              : "—"}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <PortalButton variant="primary" size="sm" onClick={save} disabled={saving} data-testid="settings-nylas-save">
          {saving ? "Saving…" : "Save"}
        </PortalButton>
        <PortalButton variant="ghost" size="sm" onClick={testConnection} data-testid="settings-nylas-test">
          Test connection
        </PortalButton>
      </div>
    </Surface>
  );
}

// --------------------------------------------------------------- Team
function TeamTab() {
  const [data, setData] = React.useState(null);
  const { user: me } = useAuth() || {};

  React.useEffect(() => {
    api
      .get("/settings/team")
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, []);

  if (!data) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-6" data-testid="settings-team-tab">
      <EditorialRule label={`${(data.members || []).length} member${(data.members || []).length === 1 ? "" : "s"}`} />
      <Surface variant="default" padded={false} className="overflow-hidden">
        {(data.members || []).length === 0 ? (
          <Empty title="No team members yet." hint="Add advisors and editors in a future release." />
        ) : (
          <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
            {data.members.map((m) => (
              <li key={m.id || m.email} className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-9 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] inline-flex items-center justify-center font-mono text-[11px] tracking-wider">
                    {(m.name || m.email || "·").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-body text-[14px] text-[hsl(var(--foreground))]">
                      {m.name || m.email}
                      {me?.email === m.email && (
                        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                          · you
                        </span>
                      )}
                    </p>
                    {m.email && (
                      <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                        {m.email}
                      </p>
                    )}
                  </div>
                </div>
                <StatusTag tone={m.role === "owner" ? "gold" : "neutral"}>{m.role}</StatusTag>
              </li>
            ))}
          </ul>
        )}
      </Surface>

      <Surface variant="paper" padded>
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
          Team management (invite, role changes, removal) ships in a future Settings release.
        </p>
      </Surface>
    </div>
  );
}

// --------------------------------------------------------------- Audit log (Pass G)
function AuditTab() {
  const [summary, setSummary] = React.useState(null);
  const [rows, setRows] = React.useState(null);
  const [actorFilter, setActorFilter] = React.useState("");

  const refresh = React.useCallback(async () => {
    setRows(null);
    const [s, r] = await Promise.all([
      api.get("/audit/summary", { params: { hours: 72 } }).then((x) => x.data).catch(() => null),
      api.get("/audit/log", { params: { limit: 60, actor: actorFilter || undefined } }).then((x) => x.data).catch(() => ({ rows: [] })),
    ]);
    setSummary(s);
    setRows(r.rows || []);
  }, [actorFilter]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-8" data-testid="settings-audit-tab">
      <EditorialRule label="Past 72 hours · activity" />
      <Surface variant="paper" padded>
        {!summary ? (
          <Skeleton className="h-12" />
        ) : summary.actions.length === 0 ? (
          <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
            Nothing recorded in the past 72 hours.
          </p>
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="settings-audit-summary">
            {summary.actions.slice(0, 8).map((a) => (
              <div key={a.action} className="px-3.5 py-2 rounded-md border border-[rgba(0,0,0,0.06)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  {a.action}
                </p>
                <p className="mt-0.5 font-serif text-lg">{a.count}</p>
              </div>
            ))}
          </div>
        )}
      </Surface>

      <div className="flex items-center gap-3">
        <input
          className={inputClass + " max-w-xs"}
          placeholder="Filter by actor email…"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          data-testid="settings-audit-actor-filter"
        />
      </div>

      <Surface variant="default" padded={false} className="overflow-hidden">
        {!rows ? (
          <Skeleton className="h-40" />
        ) : rows.length === 0 ? (
          <Empty title="No audit entries match." hint="Try a different actor or come back later." />
        ) : (
          <ul className="divide-y divide-[rgba(0,0,0,0.05)]" data-testid="settings-audit-list">
            {rows.map((r, i) => (
              <li key={`${r.timestamp}-${i}`} className="px-5 py-3.5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-body text-[13.5px]">
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-[hsl(var(--accent-gold))] mr-2">
                      {r.action}
                    </span>
                    {r.actor || "—"}
                    {r.target_id && (
                      <span className="ml-2 font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                        · {r.target_type || "item"} {String(r.target_id).slice(0, 8)}
                      </span>
                    )}
                  </p>
                  {r.details && (
                    <p className="mt-0.5 font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))] truncate">
                      {typeof r.details === "string" ? r.details : JSON.stringify(r.details).slice(0, 120)}
                    </p>
                  )}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] shrink-0">
                  {r.timestamp ? new Date(r.timestamp).toLocaleString() : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Surface>
    </div>
  );
}
